import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Users, Home, TrendingUp, Shield, Zap, BarChart3 } from 'lucide-react';
import { PricingPlans } from '@/components/pricing/PricingPlans';
import { STRIPE_PRODUCTS } from '@/stripe-config';
import { stripeService } from '@/services/stripe-service';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { metaPixel } from '@/utils/meta-pixel';

export const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const analytics = useAnalytics();
  
  // Track pricing page view with Meta Pixel
  React.useEffect(() => {
    metaPixel.trackPricingView();
  }, []);
  
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showUserForm, setShowUserForm] = useState(false);

  const handleSelectPlan = async (plan: typeof STRIPE_PRODUCTS[0]) => {
    try {
      // Redirect to auth if not authenticated
      if (!isAuthenticated) {
        navigate(`/auth?mode=signup&redirect=/pricing&plan=${plan.id}`);
        return;
      }
      
      setLoading(true);
      setError(null);
      setSelectedPlan(plan.id);

      // Track plan selection
      analytics.trackEvent({
        category: plan.mode === 'subscription' ? 'subscription' : 'payment',
        action: 'select_plan',
        label: plan.id,
        custom_parameters: {
          price: plan.price,
          plan_name: plan.name
        }
      });
      
      // Track with Meta Pixel
      if (plan.mode === 'subscription') {
        metaPixel.trackEvent('InitiateCheckout', {
          content_name: plan.name,
          content_category: 'subscription',
          value: plan.price,
          currency: 'EUR'
        });
      } else {
        metaPixel.trackEvent('InitiateCheckout', {
          content_name: plan.name,
          content_category: 'credits',
          value: plan.price,
          currency: 'EUR'
        });
      }
      
      const { stripeService } = await import('@/services/stripe-service');
      
      const baseUrl = window.location.origin;
      const successUrl = plan.mode === 'subscription' 
        ? `${baseUrl}/success?session_id={CHECKOUT_SESSION_ID}&type=subscription`
        : `${baseUrl}/success?session_id={CHECKOUT_SESSION_ID}&credits=1&type=credit`;
      const cancelUrl = `${baseUrl}/pricing`;
      
      const { url } = await stripeService.createCheckoutSession({
        priceId: plan.priceId,
        mode: plan.mode,
        successUrl,
        cancelUrl,
        creditsAmount: plan.mode === 'payment' ? 1 : undefined
      });
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error: any) {
      console.error('Purchase error:', error);
      setError(error.message || 'Erreur lors de la création de la session de paiement');
      
      // Track error
      analytics.trackEvent({
        action: 'error',
        category: 'payment',
        label: 'checkout_creation_failed',
        custom_parameters: {
          error_message: error.message,
          plan_id: plan.id
        }
      });
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Tarifs - DPEWizard</title>
        <meta name="description" content="Découvrez nos tarifs simples et transparents pour l'analyse DPE intelligente. Abonnement mensuel ou annuel (économisez 11%), ou achat à l'unité, sans engagement." />
        <link rel="canonical" href="https://www.dpewizard.com/pricing" />
      </Helmet>

      {/* Section ROI — avant les plans */}
      <section className="py-10 bg-blue-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">Quelle formule vous convient ?</h2>
            <p className="text-blue-100">Un calcul simple pour choisir sans se tromper</p>
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-center">
            <div className="bg-white/10 rounded-xl p-5">
              <div className="text-3xl font-bold mb-1">1–2</div>
              <div className="text-blue-100 text-sm mb-3">analyses par mois</div>
              <div className="text-white font-semibold">Accès ponctuel</div>
              <div className="text-blue-200 text-sm">9,90€ par analyse, sans engagement</div>
            </div>
            <div className="bg-white rounded-xl p-5 text-blue-700 shadow-lg">
              <div className="text-3xl font-bold mb-1 text-blue-600">2+</div>
              <div className="text-blue-400 text-sm mb-3">analyses par mois</div>
              <div className="text-blue-700 font-semibold">Abonnement illimité</div>
              <div className="text-blue-500 text-sm">19,90€/mois · analyses illimitées</div>
              <div className="mt-2 text-xs bg-green-100 text-green-700 rounded-full px-2 py-0.5 inline-block font-medium">
                Moins cher dès la 3e analyse
              </div>
            </div>
            <div className="bg-white/10 rounded-xl p-5">
              <div className="text-3xl font-bold mb-1">Agence</div>
              <div className="text-blue-100 text-sm mb-3">usage professionnel</div>
              <div className="text-white font-semibold">Plan Pro Agence</div>
              <div className="text-blue-200 text-sm">99€ HT/mois · PDF white-label</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Choisissez votre formule
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Que vous soyez particulier ou professionnel, nous avons la solution adaptée à vos besoins
            </p>
          </div>

          {error && (
            <div className="max-w-2xl mx-auto mb-8 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <PricingPlans
            onSelectPlan={handleSelectPlan}
            selectedPlanId={selectedPlan}
            loading={loading}
          />

          {/* Pour qui ? */}
          <div className="mt-16 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">Pour qui est fait DPEWizard ?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Home className="w-5 h-5 text-blue-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Particuliers propriétaires</h4>
                <p className="text-gray-600 text-sm">Vous avez reçu un DPE et ne savez pas quels travaux prioriser. DPEWizard vous donne un plan d'action chiffré en 2 minutes.</p>
                <div className="mt-3 text-xs text-blue-600 font-medium">→ Accès ponctuel recommandé</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Investisseurs &amp; marchands de biens</h4>
                <p className="text-gray-600 text-sm">Vous analysez plusieurs biens par mois pour évaluer leur potentiel de revalorisation ou leur conformité réglementaire.</p>
                <div className="mt-3 text-xs text-green-600 font-medium">→ Abonnement illimité recommandé</div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-900 mb-2">Agents immobiliers</h4>
                <p className="text-gray-600 text-sm">Vous conseillez des clients sur des biens énergivores et souhaitez leur remettre des rapports PDF professionnels avec votre branding.</p>
                <div className="mt-3 text-xs text-purple-600 font-medium">→ Plan Pro Agence recommandé</div>
              </div>
            </div>
          </div>

          {/* Preuves de valeur */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 text-center mb-6">Pourquoi faire confiance à DPEWizard ?</h3>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Données ADEME officielles</h4>
                  <p className="text-gray-600 text-sm">Chaque analyse s'appuie sur le registre officiel ADEME — pas de saisie manuelle, pas d'erreur de transcription.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <BarChart3 className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">28 règles métier validées</h4>
                  <p className="text-gray-600 text-sm">Notre moteur détecte les incohérences DPE et calcule l'impact réel de chaque travail, pas une simple estimation générique.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Zap className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Résultats en 2 minutes</h4>
                  <p className="text-gray-600 text-sm">Numéro DPE → questionnaire → recommandations chiffrées et priorisées, tout en moins de 2 minutes.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Questions fréquentes
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-8">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Quelle est la différence entre l'abonnement mensuel et annuel ?
              </h3>
              <p className="text-gray-600">
                L'abonnement annuel vous permet de bénéficier de 2 mois offerts par rapport au mensuel. Vous êtes facturé une fois par an (190,00€ TTC) soit l'équivalent de 15,83€/mois, contre 19,90€/mois pour l'abonnement mensuel.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Puis-je annuler mon abonnement à tout moment ?
              </h3>
              <p className="text-gray-600">
                Oui, vous pouvez annuler votre abonnement à tout moment. L'annulation prendra effet
                à la fin de votre période de facturation en cours.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Que comprend une analyse DPE complète ?
              </h3>
              <p className="text-gray-600">
                Chaque analyse inclut le calcul de la classe énergétique, l'identification des points 
                d'amélioration, des recommandations personnalisées avec ROI, et un rapport détaillé.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Les crédits ont-ils une date d'expiration ?
              </h3>
              <p className="text-gray-600">
                Non, les crédits achetés n'expirent jamais. Vous pouvez les utiliser quand vous le souhaitez.
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Proposez-vous des tarifs pour les professionnels ?
              </h3>
              <p className="text-gray-600">
                Oui, nous proposons une formule d'abonnement. Elle est évidemment orientée vers les professionnels, notamment les investisseurs locatifs, les marchands de biens et les agents immobiliers qui analysent beaucoup de DPE. 
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;